import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, map } from 'rxjs';
import { BaseFilter } from 'ag-grid-community';
import { NewsResource } from '../api-users/model/newsResource';
import { CreateNewsRequest } from '../api-users/model/createNewsRequest';
import { UpdateNewsRequest } from '../api-users/model/updateNewsRequest';
import { NewsV2Service } from '../api-users/api/newsV2.service';
import { AppHttpResourcesV2NewsNewsResource } from '../api-users/model/appHttpResourcesV2NewsNewsResource';

@Injectable({
    providedIn: 'root',
})
export class NewsService {

    private _news: BehaviorSubject<NewsResource | null> = new BehaviorSubject<NewsResource | null>(null);
    private _newss: BehaviorSubject<NewsResource[] | null> = new BehaviorSubject<NewsResource[] | null>(null);

    public actualFilter?: BaseFilter;

    constructor(
        private readonly _newsV2Service: NewsV2Service,
    ) {}

    get news$(): Observable<NewsResource | null> {
        return this._news.asObservable();
    }

    get newss$(): Observable<NewsResource[] | null> {
        return this._newss.asObservable();
    }

    public getNews(idNews: number, include?: string) {
        return this._newsV2Service.v2NewsNewsShow(idNews, include).pipe(
            tap(news => {
                this._news.next(news);
            }),
        );
    }

    public getNewss(
        page?: number,
        perPage?: number,
        forSelect?: boolean,
        sort?: string,
        include?: string,
        fields?: string,
        filter?: Record<string, unknown>
    ) {
        return this._newsV2Service.v2NewsNewsIndex(page, perPage, forSelect, sort, include, fields, filter).pipe(
            tap(response => {
                const newss = response?.data || response;
                this._newss.next(Array.isArray(newss) ? newss : []);
            }),
        );
    }

    searchNewss(
        baseFilter: BaseFilter,
        page?: number,
        perPage?: number,
        forSelect?: boolean,
        sort?: string,
        include?: string,
        fields?: string,
        filter?: Record<string, unknown>
    ): Observable<NewsResource[]> {
        this.actualFilter = baseFilter;
        return this._newsV2Service.v2NewsNewsIndex(page, perPage, forSelect, sort, include, fields, filter).pipe(
            map((response: { data?: NewsResource[] } | NewsResource[]) => {
                const newss = 'data' in response ? response.data : response;
                const result = Array.isArray(newss) ? newss : [];
                this._newss.next(result);
                return result;
            })
        );
    }

    getListNewsForAdmin(
        page?: number,
        perPage?: number,
        forSelect?: boolean,
        sort?: string,
        include?: string,
        fields?: string,
        filter?: Record<string, unknown>
    ): Observable<unknown> {
        return this._newsV2Service.getListNewsForAdmin(page, perPage, forSelect, sort, include, fields, filter);
    }

    public updateNews(editedNews: number, news: UpdateNewsRequest): Observable<AppHttpResourcesV2NewsNewsResource> {
        return this._newsV2Service.v2NewsNewsUpdate(editedNews, news);
    }

    public createNews(news: CreateNewsRequest): Observable<AppHttpResourcesV2NewsNewsResource> {
        return this._newsV2Service.v2NewsNewsStore(news);
    }

    public deleteNews(newsId: number): Observable<unknown> {
        return this._newsV2Service.v2NewsNewsDestroy(newsId);
    }

    public changeState(newsId: number, state: string): Observable<AppHttpResourcesV2NewsNewsResource> {
        return this._newsV2Service.v2NewsNewsChangeStatus(newsId, {status: state});
    }

    public getNewsRecipients(newsId: number): Observable<unknown> {
        return this._newsV2Service.getNewsRecipients(newsId);
    }

    public sendNews(newsId: number): Observable<unknown> {
        return this._newsV2Service.sendNews(newsId);
    }

}
